import React from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Button,
  Box,
  Typography,
  FormControl,
  Select,
  MenuItem,
  Radio,
  RadioGroup,
  FormControlLabel,
} from "@mui/material";
import { CirclePlay } from "lucide-react";
import type {
  FocusSessionVariant,
  MmkFocusSessionCategory,
} from "../../types/mmk";
import { FOCUS_SESSION_VARIANTS, MMK_FILTER_CATEGORIES } from "../../types/mmk";

const CATEGORY_OPTIONS = MMK_FILTER_CATEGORIES.filter(
  (c): c is MmkFocusSessionCategory => c !== "All",
);

const optionStyles = (isSelected: boolean) => ({
  border: "1px solid",
  borderColor: isSelected ? "primary.main" : "divider",
  borderRadius: 2,
  p: 1.5,
  cursor: "pointer",
  bgcolor: isSelected ? "action.selected" : "transparent",
  transition: "border-color 0.2s, background-color 0.2s",
  "&:hover": {
    borderColor: isSelected ? "primary.main" : "action.hover",
    bgcolor: isSelected ? "action.selected" : "action.hover",
  },
});

interface FocusSessionVariantModalProps {
  open: boolean;
  onClose: () => void;
  selected: FocusSessionVariant;
  onSelect: (v: FocusSessionVariant) => void;
  selectedCategory: MmkFocusSessionCategory | "";
  onCategoryChange: (c: MmkFocusSessionCategory | "") => void;
  onStart: () => void;
  canStart: boolean;
}

export const FocusSessionVariantModal: React.FC<FocusSessionVariantModalProps> = ({
  open,
  onClose,
  selected,
  onSelect,
  selectedCategory,
  onCategoryChange,
  onStart,
  canStart,
}) => {
  const handleStart = () => {
    onStart();
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: "hidden",
        },
      }}
    >
      <DialogTitle
        sx={{
          fontWeight: 600,
          fontSize: "20px",
          px: 3,
          pt: 3,
          pb: 1,
        }}
      >
        Focus Session
      </DialogTitle>
      <DialogContent sx={{ px: 3, pb: 3, pt: 0 }}>
        <RadioGroup
          value={selected}
          onChange={(e) => onSelect(e.target.value as FocusSessionVariant)}
          sx={{ gap: 1.5 }}
        >
          {FOCUS_SESSION_VARIANTS.map(({ id, label, description }) =>
            id === "by_category" ? (
              <Box
                key={id}
                onClick={() => onSelect("by_category")}
                sx={{
                  ...optionStyles(selected === "by_category"),
                  overflow: "hidden",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "flex-start" }}>
                  <Radio
                    value="by_category"
                    size="small"
                    sx={{ p: 0.5, mt: 0.25 }}
                  />
                  <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 0.5 }}>
                    <Typography fontWeight={600} fontSize="15px">
                      {label}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: "text.secondary", fontSize: "13px" }}
                    >
                      {description}
                    </Typography>
                    <FormControl fullWidth size="small" sx={{ mt: 0.5 }}>
                  <Select
                    value={selectedCategory || "placeholder"}
                    displayEmpty
                    renderValue={(v) => {
                      const val = v === "placeholder" ? "" : v;
                      return val ? String(val) : "Category";
                    }}
                    onClick={(e) => e.stopPropagation()}
                    onChange={(e) => {
                      const val = e.target.value;
                      onSelect("by_category");
                      onCategoryChange(
                        val === "placeholder"
                          ? ""
                          : (val as MmkFocusSessionCategory),
                      );
                    }}
                    sx={{
                      bgcolor: "background.paper",
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "divider",
                      },
                    }}
                  >
                    <MenuItem value="placeholder" disabled>
                      Category
                    </MenuItem>
                    {CATEGORY_OPTIONS.map((cat) => (
                      <MenuItem key={cat} value={cat}>
                        {cat}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                  </Box>
                </Box>
              </Box>
            ) : (
              <Box
                key={id}
                onClick={() => onSelect(id)}
                sx={optionStyles(selected === id)}
              >
                <FormControlLabel
                  value={id}
                  control={<Radio size="small" sx={{ p: 0.5 }} />}
                  label={
                    <Box>
                      <Typography fontWeight={600} fontSize="15px">
                        {label}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: "text.secondary",
                          fontSize: "13px",
                          mt: 0.25,
                        }}
                      >
                        {description}
                      </Typography>
                    </Box>
                  }
                  sx={{ m: 0, alignItems: "flex-start", "& .MuiFormControlLabel-label": { flex: 1 } }}
                />
              </Box>
            ),
          )}
        </RadioGroup>

        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5, mt: 3 }}>
          <Button onClick={onClose} sx={{ textTransform: "none", px: 2 }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleStart}
            disabled={!canStart}
            startIcon={<CirclePlay size={18} color="#FFF" />}
            sx={{
              textTransform: "none",
              borderRadius: 2,
              px: 2.5,
              py: 1,
              bgcolor: "#14b8a6",
              color: "#FFFFFF",
              "&:hover": { bgcolor: "#0d9488", color: "#FFFFFF" },
            }}
          >
            Start
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};
